// The Progress page's chart filters, stated once.
//
// Before df-a91d4b008864 each filter was a loose boolean on the page, and the set of filters was
// written out by hand in six places (entry defaults, series visibility, badge count, popover
// rows, toggle, popover write-back). Missing one compiled cleanly and failed silently. Every one
// of those places now loops over PROGRESS_FILTERS instead, so adding a filter is one row here.

import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';

export type ProgressChartType = 'bodyComposition' | 'nutrition';

// the ids are the popover button values the page has always used
export type ProgressFilterId =
  | 'weight'
  | 'waist'
  | 'skinfolds'
  | 'loggedCalories'
  | 'targetCalories'
  | '7dayAvg'
  | 'showPoints';

export enum ProgressFilterGroup {
  BodyComposition = 'bodyComposition',
  Nutrition = 'nutrition',
  Display = 'display',
}

export interface ProgressFilterDef {
  id: ProgressFilterId;
  group: ProgressFilterGroup;
  label: () => TranslatedString;
  // a description also puts a help icon beside the popover row
  description?: () => TranslatedString;
  // the chart series this filter shows or hides; display settings have none
  seriesId?: string;
}

export type ProgressFilterState = Record<ProgressFilterId, boolean>;

// the order of the rows is the order of the popover, within each group
export const PROGRESS_FILTERS: readonly ProgressFilterDef[] = [
  { id: 'weight', group: ProgressFilterGroup.BodyComposition, label: () => LL.common.measurements.weight(), seriesId: 'weight' },
  { id: 'waist', group: ProgressFilterGroup.BodyComposition, label: () => LL.common.measurements.waist(), seriesId: 'waist' },
  { id: 'skinfolds', group: ProgressFilterGroup.BodyComposition, label: () => LL.common.measurements.skinfolds(), seriesId: 'skinfold' },
  { id: 'loggedCalories', group: ProgressFilterGroup.Nutrition, label: () => LL.nutrition.charts.loggedCalories(), seriesId: 'consumed' },
  { id: 'targetCalories', group: ProgressFilterGroup.Nutrition, label: () => LL.nutrition.charts.targetCalories(), seriesId: 'target' },
  {
    id: '7dayAvg',
    group: ProgressFilterGroup.Display,
    label: () => LL.progress.filterOptions.showAverage(),
    description: () => LL.progress.filterOptions.showAverageDescription(),
  },
  {
    id: 'showPoints',
    group: ProgressFilterGroup.Display,
    label: () => LL.progress.filterOptions.showPoints(),
    description: () => LL.progress.filterOptions.showPointsDescription(),
  },
];

// the popover's section order and headers
export const PROGRESS_FILTER_GROUPS: readonly { group: ProgressFilterGroup; header: () => TranslatedString }[] = [
  { group: ProgressFilterGroup.BodyComposition, header: () => LL.progress.filterHeaders.bodyComposition() },
  { group: ProgressFilterGroup.Nutrition, header: () => LL.progress.filterHeaders.nutrition() },
  { group: ProgressFilterGroup.Display, header: () => LL.progress.filterHeaders.chartDisplaySettings() },
];

// the chart a data group belongs to; display settings apply to both charts
function chartTypeOfGroup(group: ProgressFilterGroup): ProgressChartType | undefined {
  switch (group) {
    case ProgressFilterGroup.BodyComposition:
      return 'bodyComposition';
    case ProgressFilterGroup.Nutrition:
      return 'nutrition';
    case ProgressFilterGroup.Display:
      return undefined;
  }
}

// whether a popover button value is one of the filter ids
export function isProgressFilterId(value: string | undefined): value is ProgressFilterId {
  return PROGRESS_FILTERS.some(f => f.id === value);
}

// the state a fresh page starts from, before any navigation parameter is applied
export function initialFilterState(): ProgressFilterState {
  return filterStateOnEnter('bodyComposition', {
    weight: false, waist: false, skinfolds: false,
    loggedCalories: false, targetCalories: false,
    '7dayAvg': false, showPoints: false,
  });
}

// the state when the page is entered showing chartType:
// that chart's filters all on, the other chart's all off, the 7-day average reset to off,
// and "show points" left as it was
export function filterStateOnEnter(chartType: ProgressChartType, previous: ProgressFilterState): ProgressFilterState {
  const state = { ...previous };
  for (const filter of PROGRESS_FILTERS) {
    const filterChart = chartTypeOfGroup(filter.group);
    if (filterChart !== undefined) {
      state[filter.id] = filterChart === chartType;
    }
  }
  state['7dayAvg'] = false;
  return state;
}

// toggle one filter. Toggling a filter of the other chart clears the current chart's filters
// and switches to that chart.
export function toggleProgressFilter(
  state: ProgressFilterState,
  id: ProgressFilterId,
  chartType: ProgressChartType,
): { state: ProgressFilterState; chartType: ProgressChartType } {
  const next = { ...state };
  let nextChartType = chartType;

  const toggled = PROGRESS_FILTERS.find(f => f.id === id);
  if (!toggled) throw new Error(`Progress filter '${id}' has no row in PROGRESS_FILTERS`);
  const toggledChart = chartTypeOfGroup(toggled.group);
  if (toggledChart !== undefined) {
    // clear every filter that belongs to the other chart
    for (const filter of PROGRESS_FILTERS) {
      const filterChart = chartTypeOfGroup(filter.group);
      if (filterChart !== undefined && filterChart !== toggledChart) {
        next[filter.id] = false;
      }
    }
    nextChartType = toggledChart;
  }

  next[id] = !next[id];
  return { state: next, chartType: nextChartType };
}

// a series no filter controls is always shown
export function isSeriesHidden(state: ProgressFilterState, seriesId: string): boolean {
  const filter = PROGRESS_FILTERS.find(f => f.seriesId === seriesId);
  return filter ? !state[filter.id] : false;
}

// the number beside the Filters button: every filter that is on
export function countActiveFilters(state: ProgressFilterState): number {
  return PROGRESS_FILTERS.filter(f => state[f.id]).length;
}
